import React, { useState } from "react";
import { flagUrlSquare } from "../../shared/flags";
import s from "./DuelBoardLab.module.scss";

// /debug/duel-board: designs for the ranked 1v1's scoreboard on a computer,
// the strip under a wide board (rectangle / stadium). Best of 5, first to 3:
// the marks for the rounds, and small player cards with their flags. The bar
// steps through the rounds. Linked from the Admin page.

const P1 = { name: "Mathias Lindblom", flag: "se", color: "#03A9F4" };
const P2 = { name: "TurboNoodle", flag: "nz", color: "#E91E63" };
// who won each round, in order (1 = P1, 2 = P2)
const SEQ = [2, 1, 1, 2, 1];

type P = typeof P1;
interface Board { played: number; wins: [number, number]; rounds: (1 | 2)[] }
function boardAt(played: number): Board {
  const rounds = SEQ.slice(0, played) as (1 | 2)[];
  return { played, rounds, wins: [rounds.filter((r) => r === 1).length, rounds.filter((r) => r === 2).length] };
}
const col = (w: 1 | 2) => (w === 1 ? P1.color : P2.color);

function Flag({ p, size = 18, round }: { p: P; size?: number; round?: boolean }) {
  return <img className={round ? s.flagRound : s.flag} src={flagUrlSquare(p.flag)} width={size} height={size} alt="" />;
}

// five marks, one per possible round, lit in each round winner's colour
function Marks({ b, cls }: { b: Board; cls: string }) {
  return (
    <span className={cls}>
      {[0, 1, 2, 3, 4].map((i) => {
        const w = b.rounds[i];
        return <i key={i} className={w ? s.on : ""} style={w ? ({ "--c": col(w) } as React.CSSProperties) : undefined} />;
      })}
    </span>
  );
}

// a player's own three slots toward "first to 3"
function Slots({ n, color, cls, reverse }: { n: number; color: string; cls: string; reverse?: boolean }) {
  const idx = reverse ? [2, 1, 0] : [0, 1, 2];
  return (
    <span className={cls}>
      {idx.map((i) => <i key={i} className={i < n ? s.on : ""} style={i < n ? ({ "--c": color } as React.CSSProperties) : undefined} />)}
    </span>
  );
}

function Card({ p, score, right, compact }: { p: P; score?: number; right?: boolean; compact?: boolean }) {
  return (
    <span className={`${s.card} ${right ? s.cardRight : ""} ${compact ? s.cardCompact : ""}`} style={{ "--c": p.color } as React.CSSProperties}>
      <Flag p={p} size={compact ? 16 : 20} round />
      <span className={s.cardName}>{p.name}</span>
      {score !== undefined && <b className={s.cardScore}>{score}</b>}
    </span>
  );
}

const DESIGNS: { id: string; name: string; blurb: string; render: (b: Board) => React.ReactNode }[] = [
  {
    id: "A", name: "Slim marks, cards either side",
    blurb: "The five marks small and centred; each player's card at their end with the flag and rounds won.",
    render: (b) => (
      <div className={s.row}>
        <Card p={P1} score={b.wins[0]} />
        <Marks b={b} cls={s.marksSlim} />
        <Card p={P2} score={b.wins[1]} right />
      </div>
    ),
  },
  {
    id: "B", name: "Versus line",
    blurb: "One centred line: flag and name, the marks, name and flag. Nothing else, the lightest.",
    render: (b) => (
      <div className={s.vsLine}>
        <span className={s.vsSide}><Flag p={P1} round /><span style={{ color: P1.color }}>{P1.name}</span></span>
        <Marks b={b} cls={s.marksPill} />
        <span className={s.vsSide}><span style={{ color: P2.color }}>{P2.name}</span><Flag p={P2} round /></span>
      </div>
    ),
  },
  {
    id: "C", name: "Race to 3",
    blurb: "No shared five: each player's own three slots toward first to 3, facing each other across a VS.",
    render: (b) => (
      <div className={s.row}>
        <Card p={P1} compact />
        <span className={s.race}>
          <Slots n={b.wins[0]} color={P1.color} cls={s.slots} reverse />
          <span className={s.vs}>vs</span>
          <Slots n={b.wins[1]} color={P2.color} cls={s.slots} />
        </span>
        <Card p={P2} compact right />
      </div>
    ),
  },
  {
    id: "C2", name: "Race to 3: arena slabs",
    blurb: "Bigger everything: slanted slabs in each player's colour with a big flag and name, three big hex slots per side, a black VS diamond in the middle.",
    render: (b) => (
      <div className={s.c2}>
        {[P1, P2].map((p, k) => (
          <React.Fragment key={k}>
            {k === 1 && <span className={s.c2Vs}><span>VS</span></span>}
            <span className={`${s.c2Side} ${k === 1 ? s.c2Right : ""}`} style={{ "--c": p.color } as React.CSSProperties}>
              <span className={s.c2Slab}><Flag p={p} size={30} round /><span className={s.c2Name}>{p.name}</span></span>
              <Slots n={b.wins[k]} color={p.color} cls={s.c2Hex} reverse={k === 0} />
            </span>
          </React.Fragment>
        ))}
      </div>
    ),
  },
  {
    id: "C3", name: "Race to 3: power bars",
    blurb: "Each player one long bar from their end to the middle, cut in three; a won round fills a third of it in their colour, like a charging meter. Flag and name ride on the bar.",
    render: (b) => (
      <div className={s.c3}>
        {[P1, P2].map((p, k) => (
          <React.Fragment key={k}>
            {k === 1 && <span className={s.c3Vs}>VS</span>}
            <span className={`${s.c3Bar} ${k === 1 ? s.c3Right : ""}`} style={{ "--c": p.color } as React.CSSProperties}>
              {[0, 1, 2].map((i) => <i key={i} className={i < b.wins[k] ? s.c3On : ""} />)}
              <span className={s.c3Label}><Flag p={p} size={24} round /><b>{p.name}</b></span>
            </span>
          </React.Fragment>
        ))}
      </div>
    ),
  },
  {
    id: "C4", name: "Race to 3: trophy at the end",
    blurb: "Big glowing rings, and the third slot on each side is a trophy: take it and you win. Flag big above the name at each end, the VS in the gap between the trophies.",
    render: (b) => (
      <div className={s.c4}>
        {[P1, P2].map((p, k) => (
          <React.Fragment key={k}>
            {k === 1 && <span className={s.c4Vs}>VS</span>}
            <span className={`${s.c4Side} ${k === 1 ? s.c4Right : ""}`} style={{ "--c": p.color } as React.CSSProperties}>
              <span className={s.c4Who}><Flag p={p} size={34} round /><b>{p.name}</b></span>
              <span className={s.c4Rings}>
                {(k === 0 ? [0, 1, 2] : [2, 1, 0]).map((i) => (
                  <i key={i} className={`${i < b.wins[k] ? s.c4On : ""} ${i === 2 ? s.c4Trophy : ""}`}>{i === 2 ? "🏆" : null}</i>
                ))}
              </span>
            </span>
          </React.Fragment>
        ))}
      </div>
    ),
  },
  {
    id: "C5", name: "Race to 3: stadium scoreboard",
    blurb: "A full-width dark scoreboard panel, lit like a stadium sign: flags and names big at the ends, three bulbs each, and the rounds-won number huge beside them.",
    render: (b) => (
      <div className={s.c5}>
        {[P1, P2].map((p, k) => (
          <React.Fragment key={k}>
            {k === 1 && <span className={s.c5Mid}><em>first to</em><b>3</b></span>}
            <span className={`${s.c5Side} ${k === 1 ? s.c5Right : ""}`} style={{ "--c": p.color } as React.CSSProperties}>
              <Flag p={p} size={36} round />
              <span className={s.c5Name}>{p.name}</span>
              <span className={s.c5Bulbs}>{[0, 1, 2].map((i) => <i key={i} className={i < b.wins[k] ? s.c5On : ""} />)}</span>
              <b className={s.c5Num}>{b.wins[k]}</b>
            </span>
          </React.Fragment>
        ))}
      </div>
    ),
  },
  {
    id: "D", name: "Score, history underneath",
    blurb: "The score as the headline (2 : 1) between the cards, the five marks small under it as the round history.",
    render: (b) => (
      <div className={s.row}>
        <Card p={P1} />
        <span className={s.scoreStack}>
          <span className={s.bigScore}><b style={{ color: P1.color }}>{b.wins[0]}</b><i>:</i><b style={{ color: P2.color }}>{b.wins[1]}</b></span>
          <Marks b={b} cls={s.marksTiny} />
        </span>
        <Card p={P2} right />
      </div>
    ),
  },
  {
    id: "E", name: "Flags in the rounds",
    blurb: "Five small round tiles, R1 to R5; a won round shows the winner's flag in their colour ring. Who took which round, at a glance.",
    render: (b) => (
      <div className={s.row}>
        <Card p={P1} compact />
        <span className={s.tiles}>
          {[0, 1, 2, 3, 4].map((i) => {
            const w = b.rounds[i];
            const p = w === 1 ? P1 : w === 2 ? P2 : null;
            return (
              <span key={i} className={`${s.tile} ${p ? s.tileOn : ""}`} style={p ? ({ "--c": p.color } as React.CSSProperties) : undefined}>
                {p ? <Flag p={p} size={22} round /> : <em>R{i + 1}</em>}
              </span>
            );
          })}
        </span>
        <Card p={P2} compact right />
      </div>
    ),
  },
  {
    id: "F", name: "Marks over a versus caption",
    blurb: "The marks medium and centred, and under them one small caption: flag, name, vs, name, flag.",
    render: (b) => (
      <div className={s.stack}>
        <Marks b={b} cls={s.marksMedium} />
        <span className={s.caption}>
          <Flag p={P1} size={14} round /><span style={{ color: P1.color }}>{P1.name}</span>
          <em>vs</em>
          <span style={{ color: P2.color }}>{P2.name}</span><Flag p={P2} size={14} round />
        </span>
      </div>
    ),
  },
  {
    id: "G", name: "Two cards, pips inside",
    blurb: "Today's slanted stripe cards, only shorter, each with its own three pips toward first to 3 in place of the number.",
    render: (b) => (
      <div className={s.stripes}>
        {[P1, P2].map((p, k) => (
          <span key={k} className={s.stripe} style={{ "--c": p.color } as React.CSSProperties}>
            <Flag p={p} size={22} round />
            <span className={s.cardName}>{p.name}</span>
            <Slots n={b.wins[k]} color={p.color} cls={s.slotsSmall} />
          </span>
        ))}
      </div>
    ),
  },
  {
    id: "H", name: "Thin track",
    blurb: "A thin segmented track across the board's width, the flags at its two ends with the rounds-won number. Quietest of all.",
    render: (b) => (
      <div className={s.track}>
        <span className={s.trackEnd}><Flag p={P1} size={18} round /><b style={{ color: P1.color }}>{b.wins[0]}</b></span>
        <Marks b={b} cls={s.marksTrack} />
        <span className={s.trackEnd}><b style={{ color: P2.color }}>{b.wins[1]}</b><Flag p={P2} size={18} round /></span>
      </div>
    ),
  },
];

export default function DuelBoardLab() {
  const [played, setPlayed] = useState(3);
  const b = boardAt(played);
  return (
    <div className={s.lab}>
      <div className={s.bar}>
        <span className={s.barLabel}>Rounds played</span>
        <div className={s.seg}>
          {[0, 1, 2, 3, 4, 5].map((n) => <button key={n} className={`${s.segBtn} ${n === played ? s.segOn : ""}`} onClick={() => setPlayed(n)}>{n}</button>)}
        </div>
        <span className={s.hint}>{P1.name} (blue) {b.wins[0]} : {b.wins[1]} {P2.name} (pink)</span>
      </div>
      {DESIGNS.map((d) => (
        <section key={d.id} className={s.section}>
          <h2 className={s.h2}><b>{d.id}</b> {d.name} <span>{d.blurb}</span></h2>
          {/* the bottom of a wide board, then the strip under it, at computer size */}
          <div className={s.frame}>
            <div className={s.boardEdge} />
            <div className={s.strip}>{d.render(b)}</div>
          </div>
        </section>
      ))}
    </div>
  );
}
